import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import EditIcon from '@/components/Icons/EditIcon';
import PlusIcon from '@/components/Icons/PlusIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import useFilter from '@/hooks/useFilter';
import { IDeleteResponse, IPageInfo } from '@/interfaces/common.model';
import { IShiftResponse } from '@/interfaces/Shits.model';
import { RootState } from '@/redux/store';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
interface ITableData {
  id: string;
  index: number;
  type: string;
  shiftName: string;
  startTime1: string;
  endTime1: string;
  checkInBufferTime1: string;
  checkOutBufferTime1: string;
}
interface IpageStates extends IPageInfo {
  [key: string]: string | number | boolean | ITableData[] | undefined;
  count: number;
  tableLoader: boolean;
  shiftData: ITableData[];
  filterData: string;
  searchQuery: '';
}
const ShiftAndTimeConfig: React.FC = () => {
  const navigate = useNavigate();
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const [pageStates, setPageStates] = useState<IpageStates>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    shiftData: [],
    filterData: '',
    searchQuery: '',
  });
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [isConfirModal, setIsConfirmModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>();
  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    // { value: 'type', label: 'Work Type' },
    { value: 'shiftName', label: 'Shift Name' },
    { value: 'startTime1', label: 'Start Time' },
    { value: 'endTime1', label: 'End Time' },
    { value: 'checkInBufferTime1', label: 'Check-In-Buffer Time' },
    { value: 'checkOutBufferTime1', label: 'Check-Out-Buffer Time' },
  ];
  const deleteActionHandler = (data: ITableData) => {
    setSelectedId(data?.id);
    toggle();
  };
  const toggle = () => {
    setIsConfirmModal(!isConfirModal);
  };
  const handleUpdate = (data: ITableData) => {
    navigate(RoutesLink.addShift, { state: { data: data } });
  };
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.deleteShift + `?shiftId=${selectedId}`,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        showToastNotification('success', response?.message);
        getShifts();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
      toggle();
    }
  };
  const actionData = [
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: handleUpdate,
    },
    {
      label: 'Delete',
      className: 'btn btn-link',
      icon: <DeleteIcon />,
      title: 'delete',
      clickHandler: deleteActionHandler,
    },
  ];
  const getShifts = async () => {
    setPageStates((prev) => ({
      ...prev,
      tableLoader: true,
      shiftData: [],
    }));
    const filter = {
      pageNumber: pageStates.currentPage,
      search: pageStates.searchQuery,
      pageSize: filterDetails?.pageSize,
    };
    try {
      const response: IShiftResponse = await callApi(
        API_URL + API_ENDPOINT.getShift + '?' + getFilterUrl(filter),
        {
          method: 'GET',
        },
      );
      setPageStates((prev) => ({ ...prev, tableLoader: false }));
      if (response?.status) {
        const newData: ITableData[] = response?.data?.shift?.map(
          (item, index) => ({
            ...item,
            index: index,
            startTime1: item?.startTime
              ? moment(item?.startTime, 'HH:mm:ss').format('hh:mm A')
              : '--',
            endTime1: item?.endTime
              ? moment(item?.endTime, 'HH:mm:ss').format('hh:mm A')
              : '--',
            checkInBufferTime1: item?.checkInBufferTime
              ? moment(item?.checkInBufferTime, 'HH:mm:ss').format('hh:mm A')
              : '--',
            checkOutBufferTime1: item?.checkOutBufferTime
              ? moment(item?.checkOutBufferTime, 'HH:mm:ss').format('hh:mm A')
              : '--',
            firstHalf1: '--',
            secondHalf1: '--',
          }),
        );
        setPageStates((prev) => ({
          ...prev,
          shiftData: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
      // toggle();
    }
  };
  useEffect(() => {
    getShifts();
  }, [filterDetails?.pageSize, pageStates.currentPage, pageStates.searchQuery]);
  const { pageChangeHandler, handleSearchChange, handleChangePageSize } =
    useFilter(pageStates, setPageStates);
  return (
    <>
      <Layout>
        <div className="content">
          <MainHeading
            title={'Shift & Time Configuration'}
            buttonText={'Add Shift & Time Config'}
            buttonIcon={<PlusIcon />}
            buttonLink={RoutesLink.addShift}
          />
          <CardLayout isGrid={tableView === 'grid'}>
            <FilterComponent handleSearch={handleSearchChange} isGrid={false} />
            <CommonTable<ITableData>
              ActionData={actionData}
              pageChangeHandler={pageChangeHandler}
              tableHeading={tableHeading}
              tableData={pageStates?.shiftData}
              currentPage={pageStates?.currentPage}
              isLoading={pageStates?.tableLoader}
              totalPages={pageStates.totalPages}
              count={pageStates.count}
              handleChangePageSize={handleChangePageSize}
              pageSize={filterDetails.pageSize}
            />
          </CardLayout>
        </div>
      </Layout>
      <ConfirmModal
        isOpen={isConfirModal}
        toggle={toggle}
        submitHandler={handleDelete}
        isLoading={isLoading}
      />
    </>
  );
};
export default ShiftAndTimeConfig;
